import debugMessage from '../helpers/debugMessage';
import getPageType from '../helpers/getPageType';
import isBot from '../helpers/isBot';
import isMobile from '../helpers/isMobile';

const DEVICE_TYPE_DESKTOP = 'desktop';
const DEVICE_TYPE_MOBILE = 'mobile';
const PLATFORM_WEB = 'web';

const DEBUG_NAMESPACE = 'metrics';

const RETRY_ATTEMPTS = 3;
const THROTTLING_THRESHOLD = 500;
// Our records are small (one or two keys, about 60-120 bytes), so 200 entries stay around 15-25 KB,
// under the 64 KB limit of sendBeacon and fetch keepalive. This is not a byte limit.
const MAX_ENTRIES_PER_REQUEST = 200;

type TMetrics = Record<string, number>;

export type TMetricTags = {
  /** GPT `pos` of the ad slot, for per-slot metrics */
  position?: string;
};

// What is sent per entry: the metric values plus `position` and `pageType` tags.
type TMetricEntry = Record<string, number | string>;

export type MetricsClientConstructorParams = {
  virtualUrl: string;
  endpointUrl: string;
};

const toChunks = (metrics: TMetricEntry[]): TMetricEntry[][] => {
  const chunks: TMetricEntry[][] = [];
  for (let i = 0; i < metrics.length; i += MAX_ENTRIES_PER_REQUEST) {
    chunks.push(metrics.slice(i, i + MAX_ENTRIES_PER_REQUEST));
  }
  return chunks;
};

export default class MetricsClient {
  private pageLoaded = false;

  private queue: TMetricEntry[] = [];

  private throttlingTimer: ReturnType<typeof setTimeout> | undefined;

  private endpointUrl: string;
  private virtualUrl: string;
  // Bots never get banners (no consent decision), so their page views would only skew the ratios.
  private isBotUserAgent = isBot();

  constructor({ virtualUrl, endpointUrl }: MetricsClientConstructorParams) {
    this.virtualUrl = virtualUrl;
    this.endpointUrl = endpointUrl;
    if (document.readyState === 'complete') {
      this.onPageLoaded();
    } else {
      window.addEventListener('load', this.onPageLoaded);
    }
    // Users often leave before the throttled send; flush what is queued, also before window.load.
    window.addEventListener('pagehide', this.flushOnExit);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flushOnExit();
      }
    });
  }

  /**
   * Used for tracking performance
   *
   * @param label - `bidsRequested`
   * @param namespace - `home_top` or 'grafana_metrics' etc, only used for debug messages
   * @param tags - `position` of the ad slot, for per-slot metrics
   */
  public addPerformanceMetric = (
    label: string,
    namespace: string = '',
    tags: TMetricTags = {},
  ): void | Promise<void> => {
    if (this.isDisabled()) {
      return;
    }

    const metrics = {
      [label]: Math.round(window.performance.now()),
    };

    debugMessage(namespace, `Adding performance metric`, metrics);
    this.sendMetric(metrics, tags);
  };

  /**
   * Used for tracking multiple metrics with custom values
   * @param metrics
   * @param namespace - only used for debug messages
   * @param tags - `position` of the ad slot, for per-slot metrics
   * @returns void
   */
  public addMetrics = (metrics: TMetrics, namespace: string = '', tags: TMetricTags = {}) => {
    if (this.isDisabled()) {
      return;
    }
    debugMessage(namespace, 'Adding metric', metrics);
    this.sendMetric(metrics, tags);
  };

  /**
   * Used for disabling sending metrics when there is no endpointUrl or virtualUrl, or for bots
   */
  private isDisabled = (): boolean => {
    return !this.virtualUrl || !this.endpointUrl || this.isBotUserAgent;
  };

  private onPageLoaded = () => {
    this.pageLoaded = true;
    this.sendQueuedMetrics();
  };

  private getUrl = () => `${this.endpointUrl}?${new URLSearchParams({ platform: PLATFORM_WEB })}`;

  private getBody = (metrics: TMetricEntry[]) =>
    JSON.stringify({
      virtualUrl: this.virtualUrl,
      deviceType: isMobile() ? DEVICE_TYPE_MOBILE : DEVICE_TYPE_DESKTOP,
      metrics,
    });

  /**
   * Retries network errors and 5xx. Drops the batch on 4xx and after the last attempt:
   * re-queueing would duplicate batches the server already received and keep re-sending a rejected one.
   */
  private postMetrics = async (metrics: TMetricEntry[]) => {
    for (let attempt = 1; attempt <= RETRY_ATTEMPTS; attempt += 1) {
      if (attempt > 1) {
        const delayDuration = THROTTLING_THRESHOLD * attempt;
        debugMessage(DEBUG_NAMESPACE, `Retry #${attempt} in ${delayDuration}ms`, metrics);
        await this.delay(delayDuration);
      }
      try {
        debugMessage(DEBUG_NAMESPACE, `Posting metrics to ${this.endpointUrl} endpoint`, metrics);
        const response = await fetch(this.getUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: this.getBody(metrics),
        });

        if (response.status < 400) {
          debugMessage(DEBUG_NAMESPACE, `Metrics sent`, metrics);
          return;
        }
        if (response.status < 500) {
          debugMessage(DEBUG_NAMESPACE, `Metrics rejected, dropping them`, metrics, response);
          return;
        }
        debugMessage(DEBUG_NAMESPACE, `Sending metrics endpoint failed`, metrics, response);
      } catch (error) {
        debugMessage(DEBUG_NAMESPACE, `Sending metrics endpoint failed`, metrics, error);
      }
    }
    debugMessage(DEBUG_NAMESPACE, `Dropping metrics after ${RETRY_ATTEMPTS} attempts`, metrics);
  };

  /**
   * Sends everything queued right away, without waiting for the response (the page is going away).
   */
  private flushOnExit = () => {
    if (this.isDisabled() || this.queue.length === 0) {
      return;
    }
    const metricsToSend = this.queue;
    this.queue = [];

    toChunks(metricsToSend).forEach((chunk) => {
      const body = this.getBody(chunk);
      // String, not Blob: some in-app browsers (iOS TikTok) send Blob beacon bodies empty.
      // metrics-frontend parses the body as JSON whatever the Content-Type.
      const sent = navigator.sendBeacon?.(this.getUrl(), body);
      if (!sent) {
        fetch(this.getUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
          keepalive: true,
        }).catch((error) => debugMessage(DEBUG_NAMESPACE, `Sending metrics on exit failed`, chunk, error));
      }
    });
  };

  /**
   * Stores metrics in queue and calls sending queued metrics
   */
  private sendMetric = (metric: TMetrics, { position }: TMetricTags) => {
    // Read per entry when queued, so metrics after a client-side navigation get the new page type.
    const pageType = getPageType();
    this.queue.push({ ...metric, ...(position && { position }), ...(pageType && { pageType }) });
    this.sendQueuedMetrics();
  };

  /**
   * Sends queued metrics with throttle
   */
  private sendQueuedMetrics = () => {
    if (!this.pageLoaded || this.isDisabled() || this.queue.length === 0 || this.throttlingTimer) {
      return;
    }
    this.throttlingTimer = setTimeout(async () => {
      // Take one chunk only: the rest stays in the queue, so flushOnExit still sends it while this one is in flight.
      const chunk = this.queue.splice(0, MAX_ENTRIES_PER_REQUEST);
      // Empty when flushOnExit already sent the queue during the throttle window.
      if (chunk.length > 0) {
        await this.postMetrics(chunk);
      }

      this.throttlingTimer = undefined;
      // remaining or newly added metrics - start new throttling
      this.sendQueuedMetrics();
    }, THROTTLING_THRESHOLD);
  };

  /**
   * Returns promise with delay
   */
  private delay = (ms: number) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  };
}
